import { useState, useEffect } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PackageSearch, CheckCircle2, Circle, Truck, Link2 } from "lucide-react";
import { Container, Reveal, SectionHeading, PageBanner } from "@/site/ui";

export default function TrackOrder() {
  const c = useOutletContext();
  const t = c.track || {};
  const stages = t.stages || [];
  const [params] = useSearchParams();
  const [form, setForm] = useState({ order_code: params.get("id") || "", phone: params.get("phone") || "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runTrack = async (order_code, phone = "") => {
    if (!order_code.trim()) { toast.error("Please enter your order ID"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/site/track", { order_code, phone });
      setResult(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Order not found"); }
    finally { setBusy(false); }
  };

  const submit = (e) => { e.preventDefault(); runTrack(form.order_code, form.phone); };

  // Auto-track when arriving via a shared link (?id=RGH-00001)
  useEffect(() => {
    const id = params.get("id");
    if (id) runTrack(id, params.get("phone") || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyLink = () => {
    const url = `${window.location.origin}/track-order?id=${encodeURIComponent(result.order_code)}`;
    navigator.clipboard?.writeText(url);
    toast.success("Tracking link copied");
  };

  const currentIndex = result ? stages.indexOf(result.status) : -1;
  const input = "w-full rounded-xl border border-[#E5E5E5] bg-white p-4 text-sm text-[#0A0A0A] placeholder:text-[#A3A3A3] outline-none transition-colors duration-200 focus:border-[var(--rgh-primary)]";

  return (
    <div data-testid="track-order-page">
      <PageBanner image={c.banners?.track} eyebrow="Track Order" title={t.heading || "Track your order"} subtitle={t.subtitle} />

      <section className="py-16 md:py-24">
        <Container className="max-w-3xl">
          <Reveal>
            <form onSubmit={submit} data-testid="track-form" className="rounded-[28px] border border-gray-100 bg-white p-6 sm:p-8 shadow-sm">
              <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-4 items-end">
                <div>
                  <label className="block text-xs font-medium mb-2 text-[#525252]">Order ID *</label>
                  <input data-testid="track-code" className={input} value={form.order_code} onChange={(e) => set("order_code", e.target.value)} placeholder="e.g. RGH-00001" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-2 text-[#525252]">Phone (optional)</label>
                  <input data-testid="track-phone" className={input} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+234 …" />
                </div>
                <button type="submit" disabled={busy} data-testid="track-submit"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--rgh-primary)] px-6 py-4 text-sm font-semibold text-white transition-[transform,background-color] duration-200 hover:bg-[var(--rgh-primary-dark)] disabled:opacity-60">
                  <PackageSearch size={17} /> {busy ? "Checking…" : "Track"}
                </button>
              </div>
            </form>
          </Reveal>

          {result && (
            <Reveal delay={0.05}>
              <div data-testid="track-result" className="mt-8 rounded-[28px] border border-gray-100 bg-white p-6 sm:p-8 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-100 pb-6">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#525252]">Order</p>
                    <p className="mt-1 text-xl font-bold">{result.order_code}</p>
                    <p className="mt-1 text-sm text-[#525252]">{result.patient_name}{result.device ? ` · ${result.device}` : ""}</p>
                  </div>
                  <span className="inline-flex items-center gap-2 rounded-full bg-[rgb(var(--rgh-primary-rgb)/0.08)] px-4 py-2 text-sm font-semibold text-[var(--rgh-primary)]">
                    <Truck size={15} /> {result.status}
                  </span>
                </div>

                <button onClick={copyLink} data-testid="track-copy-link"
                  className="mt-5 inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-[#525252] transition-colors duration-200 hover:border-[var(--rgh-primary)] hover:text-[var(--rgh-primary)]">
                  <Link2 size={15} /> Copy tracking link
                </button>

                <ol className="mt-8 space-y-0">
                  {stages.map((s, i) => {
                    const done = currentIndex >= 0 && i <= currentIndex;
                    const active = i === currentIndex;
                    return (
                      <li key={s} data-testid={`track-stage-${i}`} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          {done ? <CheckCircle2 size={22} className="text-[var(--rgh-primary)]" /> : <Circle size={22} className="text-gray-300" />}
                          {i < stages.length - 1 && <span className={`w-px flex-1 my-1 ${i < currentIndex ? "bg-[var(--rgh-primary)]" : "bg-gray-200"}`} style={{ minHeight: 28 }} />}
                        </div>
                        <div className={`pb-6 ${active ? "" : "opacity-80"}`}>
                          <p className={`text-sm font-semibold ${done ? "text-[#0A0A0A]" : "text-[#A3A3A3]"}`}>{s}</p>
                        </div>
                      </li>
                    );
                  })}
                </ol>

                {result.eta && <p className="text-sm text-[#525252]">Estimated ready date: <span className="font-semibold text-[#0A0A0A]">{result.eta}</span></p>}
                {Array.isArray(result.history) && result.history.length > 0 && (
                  <div className="mt-6 border-t border-gray-100 pt-6">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#525252] mb-3">Updates</p>
                    <ul className="space-y-2">
                      {[...result.history].reverse().map((h, i) => (
                        <li key={i} className="text-sm text-[#525252]">
                          <span className="font-medium text-[#0A0A0A]">{h.status}</span>
                          {h.note ? ` — ${h.note}` : ""} <span className="text-xs text-[#A3A3A3]">· {new Date(h.at).toLocaleDateString()}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Reveal>
          )}
        </Container>
      </section>
    </div>
  );
}
